import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, List, ListItem, ListItemButton, ListItemIcon } from '@mui/material';
import { Button, Flex, Loading, SubPage, Typo } from '../../../ui';
import { useFeatures, useSettings } from '../../settings/hooks/useSettingsState';
import { useSettingsActions } from '../../settings/hooks/useSettingsActions';
import { useRoles, useIsAdmin, useIsIncarnated } from '../../auth/hooks';
import { CONTENT_SMALL } from '../../../utils/media';

// Beta features with granular role-based access control
// allowedRoles: specify exactly which roles can see each feature
const BETA_FEATURES = [
  { key: 'modernCourseBuilder', allowedRoles: ['teacher', 'owner', 'assistant', 'dark-admin'] },
  { key: 'addTeacherToCourse', allowedRoles: ['assistant', 'dark-admin'] },
  { key: 'salesExport', allowedRoles: ['assistant', 'dark-admin'] },
  { key: 'newDashboard', allowedRoles: ['assistant', 'dark-admin'] },
  { key: 'draftCourses', allowedRoles: ['assistant', 'dark-admin'] },
  { key: 'biDashboard', allowedRoles: ['assistant', 'dark-admin'] },
  { key: 'coursioNetwork', allowedRoles: ['assistant', 'dark-admin'] },
  { key: 'networkCatalog', allowedRoles: ['assistant', 'dark-admin'] },
  { key: 'campaigns', allowedRoles: ['assistant', 'dark-admin'] },
  { key: 'courseGuide', allowedRoles: ['assistant', 'dark-admin'] },
  { key: 'classroom', allowedRoles: ['assistant', 'dark-admin'] },
];

const BETA_FEATURE_KEYS = new Set(BETA_FEATURES.map((f) => f.key));
const ADMIN_ONLY_TOGGLE_FEATURE_KEYS = new Set(['modernCourseBuilder']);

const FeatureList = ({ features, localFeatures, onToggle, isFeatureDisabled, t }) => (
  <List sx={{ width: '100%', maxWidth: 360 }}>
    {features.map(({ key }) => {
      const labelId = `checkbox-list-label-${key}`;
      const disabled = isFeatureDisabled(key);

      return (
        <ListItem key={key} disablePadding>
          <ListItemButton role={undefined} onClick={() => onToggle(key)} dense disabled={disabled}>
            <ListItemIcon>
              <Checkbox edge='start' checked={!!localFeatures[key]} tabIndex={-1} disableRipple disabled={disabled} inputProps={{ 'aria-labelledby': labelId }} />
            </ListItemIcon>
            <Typo.Text>{t(`settings.${key}`)}</Typo.Text>
          </ListItemButton>
        </ListItem>
      );
    })}
  </List>
);

const ProfileBetaFeatures = () => {
  const { t } = useTranslation();
  const { isLoading, settings } = useSettings();
  const features = useFeatures();
  const savedFeatures = settings?.features || {};
  const { updateSettings } = useSettingsActions();

  const roles = useRoles();
  const isIncarnated = useIsIncarnated();
  const isAdmin = useIsAdmin();
  const canSeeAdminOnly = isIncarnated || isAdmin;

  const [localFeatures, setLocalFeatures] = useState({});

  // Initialize local features from BE data
  useEffect(() => {
    const initializedFeatures = {};

    BETA_FEATURES.forEach(({ key }) => {
      initializedFeatures[key] = features[key] === true;
    });

    setLocalFeatures(initializedFeatures);
  }, [features]);

  // Filter beta features based on user's role
  const visibleBetaFeatures = useMemo(() => {
    return BETA_FEATURES.filter(({ allowedRoles }) => {
      if (canSeeAdminOnly) return true;
      return roles?.some((role) => allowedRoles.includes(role));
    });
  }, [roles, canSeeAdminOnly]);

  const isFeatureDisabled = (key) => {
    return ADMIN_ONLY_TOGGLE_FEATURE_KEYS.has(key) && !isAdmin;
  };

  const handleToggle = (key) => {
    if (isFeatureDisabled(key)) return;

    setLocalFeatures((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSave = async () => {
    const featuresToSave = {};

    // Preserve all non-beta features that are currently enabled
    if (savedFeatures) {
      Object.entries(savedFeatures).forEach(([key, value]) => {
        if (!BETA_FEATURE_KEYS.has(key) && value === true) {
          featuresToSave[key] = true;
        }
      });
    }

    // Add beta features from local state (explicitly send false for disabled)
    BETA_FEATURES.forEach(({ key }) => {
      if (isFeatureDisabled(key)) {
        featuresToSave[key] = features[key] === true;
        return;
      }

      featuresToSave[key] = localFeatures[key] === true;
    });

    await updateSettings({ features: featuresToSave });
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <SubPage size={CONTENT_SMALL} gap='20' align='flex-start'>
      <Flex gap='10' direction='column' align='flex-start'>
        <Typo.TextTitle>{t('profile.betaFeatures_title')}</Typo.TextTitle>
        <Typo.MediumText>{t('profile.betaFeatures_description')}</Typo.MediumText>
      </Flex>
      {visibleBetaFeatures.length > 0 ? (
        <>
          <FeatureList features={visibleBetaFeatures} localFeatures={localFeatures} onToggle={handleToggle} isFeatureDisabled={isFeatureDisabled} t={t} />
          <Flex direction='row' gap='10' width='100%' justify='flex-end'>
            <Button onClick={handleSave}>{t('globals.save')}</Button>
          </Flex>
        </>
      ) : (
        <Typo.MediumText>{t('profile.betaFeatures_noFeatures')}</Typo.MediumText>
      )}
    </SubPage>
  );
};

export default ProfileBetaFeatures;
